/**
 * ScheduledRunEmailToggle
 *
 * Dropdown menu controls for scheduled-test-run email notifications:
 *  - an on/off toggle (opt-in), and
 *  - when on, a delivery-mode choice: every run vs a single daily summary.
 * Self-contained: loads the current preference on mount and persists changes.
 */

import { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getNotificationPreferences,
  setScheduledRunEmailEnabled,
  setScheduledRunEmailMode,
} from '../services/notificationPreferenceApi';

const MODE_PER_RUN = 'PER_RUN';
const MODE_DAILY_DIGEST = 'DAILY_DIGEST';

const ScheduledRunEmailToggle = () => {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState(MODE_PER_RUN);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getNotificationPreferences();
        if (active) {
          setEnabled(!!res?.scheduled_run_email_enabled);
          if (res?.scheduled_run_email_mode) setMode(res.scheduled_run_email_mode);
        }
      } catch (e) {
        // Non-fatal: leave defaults if it can't be loaded.
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (saving || loading) return;
    const next = !enabled;
    setSaving(true);
    setEnabled(next); // optimistic
    try {
      await setScheduledRunEmailEnabled(next);
      toast.success(next ? 'Scheduled-run emails turned on' : 'Scheduled-run emails turned off');
    } catch (err) {
      setEnabled(!next); // revert
      toast.error('Could not update email preference');
    } finally {
      setSaving(false);
    }
  };

  const handleModeChange = async (e, nextMode) => {
    e.stopPropagation();
    if (saving || loading || nextMode === mode) return;
    const prev = mode;
    setSaving(true);
    setMode(nextMode); // optimistic
    try {
      await setScheduledRunEmailMode(nextMode);
      toast.success(
        nextMode === MODE_DAILY_DIGEST ? 'Switched to a daily summary email' : 'Switched to per-run emails'
      );
    } catch (err) {
      setMode(prev); // revert
      toast.error('Could not update email mode');
    } finally {
      setSaving(false);
    }
  };

  const segBtn = (active) =>
    `flex-1 px-2 py-1 text-xs whitespace-nowrap rounded-md transition-colors ${
      active ? 'bg-primary text-white' : 'text-foreground-secondary hover:bg-card-hover'
    }`;

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading || saving}
        className="flex items-center justify-between gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-card-hover transition-colors disabled:opacity-60"
        title="Get an email each time a scheduled test finishes"
      >
        <span className="flex items-center gap-3 whitespace-nowrap">
          <Mail className="w-4 h-4 text-muted" />
          Scheduled-run emails
        </span>
        <span
          aria-hidden="true"
          className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${
            enabled ? 'bg-primary' : 'bg-border'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </span>
      </button>

      {enabled && (
        <div className="px-4 pb-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 bg-background border border-border rounded-lg p-0.5">
            <button
              type="button"
              disabled={saving}
              className={segBtn(mode === MODE_PER_RUN)}
              onClick={(e) => handleModeChange(e, MODE_PER_RUN)}
            >
              Every run
            </button>
            <button
              type="button"
              disabled={saving}
              className={segBtn(mode === MODE_DAILY_DIGEST)}
              onClick={(e) => handleModeChange(e, MODE_DAILY_DIGEST)}
            >
              Daily summary
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledRunEmailToggle;
